export const CATEGORIES = [
  { id: 'personal', label: 'Personal' },
  { id: 'business', label: 'Business' }
];

export const FREQUENCIES = [
  { id: 'weekly', label: 'Weekly' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'quarterly', label: 'Quarterly' },
  { id: 'yearly', label: 'Yearly' }
];

// Monthly equivalent expressed in twelfths of the original amount, so every
// frequency is an exact integer multiple and nothing is rounded until the end:
// weekly = x * 52 / 12, monthly = x * 12 / 12, quarterly = x * 4 / 12, yearly = x * 1 / 12.
const TWELFTHS = { weekly: 52, monthly: 12, quarterly: 4, yearly: 1 };
const MONTH_STEP = { monthly: 1, quarterly: 3, yearly: 12 };
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const pad = (value) => String(value).padStart(2, '0');

/* ---------- Money (integer cents) ---------- */

export function monthlyTwelfths(expense) {
  return expense.amountCents * TWELFTHS[expense.frequency];
}

// Sums exact twelfths, then rounds once (half up) to whole cents.
export function monthlyBurnCents(expenses) {
  const twelfths = expenses.reduce((sum, expense) => sum + monthlyTwelfths(expense), 0);
  return Math.floor((twelfths + 6) / 12);
}

export function formatCents(cents) {
  const dollars = Math.floor(cents / 100);
  return `$${dollars.toLocaleString('en-US')}.${pad(cents % 100)}`;
}

export function centsToInput(cents) {
  return `${Math.floor(cents / 100)}.${pad(cents % 100)}`;
}

// Parses text such as "240", "$1,200.5" or "25.00" into integer cents without floats.
export function parseAmountToCents(text) {
  const cleaned = String(text || '').trim().replace(/^\$/, '').replace(/,/g, '');
  const match = /^(\d{1,9})(?:\.(\d{1,2}))?$/.exec(cleaned);
  if (!match) return null;
  const cents = Number(match[1]) * 100 + Number((match[2] || '').padEnd(2, '0'));
  return cents > 0 ? cents : null;
}

/* ---------- Dates (date-only, no timezone conversion) ---------- */

function isLeapYear(year) {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function daysInMonth(year, month) {
  return [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
}

export function parseYmd(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1000 || month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) return null;
  return { y: year, m: month, d: day };
}

function toYmd({ y, m, d }) {
  return `${String(y).padStart(4, '0')}-${pad(m)}-${pad(d)}`;
}

function dayNumber({ y, m, d }) {
  return Math.floor(Date.UTC(y, m - 1, d) / 86400000);
}

function fromDayNumber(number) {
  const date = new Date(number * 86400000);
  return { y: date.getUTCFullYear(), m: date.getUTCMonth() + 1, d: date.getUTCDate() };
}

export function todayYmd() {
  const now = new Date();
  return toYmd({ y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate() });
}

export function formatYmd(value) {
  const parts = parseYmd(value);
  return parts ? `${MONTHS[parts.m - 1]} ${parts.d}, ${parts.y}` : '—';
}

// Next occurrence on or after `today`, always computed from the original anchor
// so end-of-month and leap-day schedules never drift. The anchor is never modified.
export function nextDueDate(anchorValue, frequency, todayValue = todayYmd()) {
  const anchor = parseYmd(anchorValue);
  const today = parseYmd(todayValue);
  if (!anchor || !today) return null;
  if (dayNumber(anchor) >= dayNumber(today)) return toYmd(anchor);

  if (frequency === 'weekly') {
    const elapsed = dayNumber(today) - dayNumber(anchor);
    return toYmd(fromDayNumber(dayNumber(anchor) + Math.ceil(elapsed / 7) * 7));
  }

  const step = MONTH_STEP[frequency];
  if (!step) return null;
  const baseIndex = anchor.y * 12 + anchor.m - 1;
  const monthsElapsed = today.y * 12 + today.m - 1 - baseIndex;
  let occurrence = Math.floor(monthsElapsed / step);
  for (;;) {
    const index = baseIndex + occurrence * step;
    const year = Math.floor(index / 12);
    const month = (index % 12) + 1;
    const candidate = { y: year, m: month, d: Math.min(anchor.d, daysInMonth(year, month)) };
    if (dayNumber(candidate) >= dayNumber(today)) return toYmd(candidate);
    occurrence += 1;
  }
}

// Adds `nextDue` and sorts by it, then name, then id.
export function sortByNextDue(expenses, todayValue = todayYmd()) {
  return expenses
    .map((expense) => ({ ...expense, nextDue: nextDueDate(expense.anchorDate, expense.frequency, todayValue) }))
    .sort((a, b) => {
      if (a.nextDue !== b.nextDue) {
        if (!a.nextDue) return 1;
        if (!b.nextDue) return -1;
        return a.nextDue < b.nextDue ? -1 : 1;
      }
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase()) || a.id.localeCompare(b.id);
    });
}